import React, { useEffect } from "react";
import { LockOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../CustomAntdComponents/Button";
import {
  trackPaywallUpgradeClicked,
  trackPaywallViewed,
} from "../../../analytics/productEvents";
import { Paths } from "../../../router/paths";
import { useAuthEntryPath } from "../../../router/useAuthEntryPath";
import { useShouldHideSubscriptions } from "../../../hooks/useShouldHideSubscriptions";
import { useTrialOffer } from "../../../hooks/useTrialOffer";
import styles from "./LockDisplay.module.css";

interface LockDisplayProps {
  /** Optional heading shown above the body text (e.g. locked stock ticker). */
  title?: string;
  text?: string;
  buttonText?: string;
  height?: string;
  /** When 402, shows upgrade limit message and navigates to subscription matrix */
  status?: 401 | 402;
  /** When provided, shows a "Watch Demo" button linking to this URL */
  demoUrl?: string;
  featureKey?: string;
  category?: string;
}

export const LockDisplay: React.FC<LockDisplayProps> = ({
  title,
  text,
  buttonText,
  height = "calc(100vh - 150px)",
  status,
  demoUrl,
  featureKey,
  category,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loginPath = useAuthEntryPath(Paths.LOGIN);
  const { symbol } = useParams<{ symbol?: string }>();
  const hideSubscriptions = useShouldHideSubscriptions();
  const isUpgrade = status === 402;
  // An eligible Free user is offered the trial instead of an upgrade (docs/free-trial-design.md).
  const trialOffer = useTrialOffer();
  const offerTrial = isUpgrade && trialOffer.isEligible;

  useEffect(() => {
    if (!isUpgrade) return;
    trackPaywallViewed({
      feature_key: featureKey,
      symbol,
      category,
      surface: window.location.pathname,
      trial_offer: offerTrial,
    });
  }, [isUpgrade, featureKey, symbol, category, offerTrial]);

  const hideUpgradeCta = isUpgrade && hideSubscriptions;
  const displayText =
    text ??
    (offerTrial
      ? t("pages.lockDisplay.trialPrompt")
      : isUpgrade
        ? t("pages.lockDisplay.upgradePrompt")
        : t("pages.lockDisplay.loginPrompt"));
  // A caller-supplied text (e.g. which plan a report needs) keeps its message and gets the
  // trial offer as a second line.
  const trialHint = offerTrial && text ? t("pages.lockDisplay.trialHint") : null;
  const displayButtonText =
    offerTrial
      ? t("pages.lockDisplay.trialButton")
      : (buttonText ??
        (isUpgrade
          ? t("pages.lockDisplay.upgradeButton")
          : t("pages.lockDisplay.loginButton")));

  const handleClick = () => {
    if (isUpgrade) {
      trackPaywallUpgradeClicked({
        feature_key: featureKey,
        symbol,
        category,
        destination: Paths.SUBSCRIPTIONS,
        trial_offer: offerTrial,
      });
      navigate(Paths.SUBSCRIPTIONS);
    } else {
      navigate(loginPath);
    }
  };

  return (
    <div className={styles.wrapper} style={{ height }}>
      <div className={styles.content}>
        <div className={styles.iconWrapper}>
          <LockOutlined className={styles.icon} />
        </div>
        <div className={styles.textWrapper}>
          {title && <h2 className={styles.title}>{title}</h2>}
          <p className={styles.text}>{displayText}</p>
          {trialHint && <p className={styles.text}>{trialHint}</p>}
          {!hideUpgradeCta && (
            <Button
              type="primary"
              size="large"
              onClick={handleClick}
              className={`${styles.button} ${demoUrl ? styles.buttonSized : ""}`}
            >
              {displayButtonText}
            </Button>
          )}
          {demoUrl && (
            <Button
              type="default"
              size="large"
              href={demoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.demoButton}
            >
              {t("pages.lockDisplay.watchDemo")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
